import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/app/components/auth/AuthProvider';
import { Avatar } from '../components/Avatar';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { PacienteBottomNav } from '@/app/components/paciente/PacienteBottomNav';
import { PacienteSidebar } from '@/app/components/paciente/PacienteSidebar';
import { PacienteHeaderDesktop } from '@/app/components/paciente/PacienteHeaderDesktop';
import {
  useProfilePaciente,
  type ProfilePaciente,
} from '@/lib/supabase/queries/useProfilePaciente';

export function PerfilPaciente() {
  const { data, error, loading } = useProfilePaciente();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-background transition-colors duration-200">
      <PacienteHeaderDesktop />

      <div className="flex-1 flex justify-center lg:justify-start">
        <PacienteSidebar />

        <div className="w-full max-w-[480px] lg:max-w-2xl flex flex-col pb-24 lg:pb-8 lg:flex-1 lg:min-w-0">
          <div className="p-6 lg:px-8 lg:pt-6 space-y-6">
        <h1 className="text-2xl lg:text-2xl lg:font-bold lg:text-primary">Perfil</h1>

        {loading && <IdentityCardSkeleton />}
        {error && !loading && <IdentityCardError message={error} />}
        {data && !loading && !error && <IdentityCard profile={data} />}

        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-[0.08em] text-muted-foreground px-2 mb-3">Configuración</h3>

          {/* Showcase: Notificaciones, Privacidad y Ayuda eran pantallas
              maqueta con botones muertos; se sacaron los accesos. */}
          <div className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" strokeWidth={1.5} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span className="font-medium">Modo oscuro</span>
            </div>
            <DarkModeToggle />
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="w-full bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] flex items-center justify-between transition-all duration-200 ease-out hover:bg-destructive/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center">
              <LogOut className="w-5 h-5 text-destructive" strokeWidth={1.5} />
            </div>
            <span className="font-medium text-destructive">Cerrar sesión</span>
          </div>
        </button>

        <p className="text-xs text-center text-muted-foreground mt-8">
          Gonix v1.0.0
        </p>
          </div>
        </div>
      </div>

      <PacienteBottomNav />
    </div>
  );
}

// ─── Identity card (real data) ──────────────────────────────────────────

function IdentityCard({ profile }: { profile: ProfilePaciente }) {
  const nombreCompleto = `${profile.paciente.nombre} ${profile.paciente.apellido}`;
  const edadLabel = formatEdad(profile.paciente.fecha_nacimiento);
  const kineLabel = profile.kine?.nombre ?? '—';
  // Sprint 3 Approach A: la UI del paciente no expone "tratamiento". La
  // fecha del contenedor invisible coincide con el inicio del primer plan
  // (RPC 0017 crea ambos al momento de la primera prescripción), así que
  // sirve como proxy para "Inicio del plan" sin fetch adicional.
  const fechaInicioLabel = profile.tratamiento
    ? formatFechaInicio(profile.tratamiento.fecha_inicio)
    : null;

  return (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center gap-4 mb-6">
        <Avatar name={nombreCompleto} size="lg" colorIndex={0} />
        <div>
          <h2 className="text-xl font-semibold">{nombreCompleto}</h2>
          <p className="text-sm text-muted-foreground">{edadLabel}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between py-3 border-b border-border">
          <span className="text-sm text-muted-foreground">Kinesiólogo</span>
          <span className="text-sm font-medium">{kineLabel}</span>
        </div>
        <div className="flex justify-between py-3">
          <span className="text-sm text-muted-foreground">Inicio del plan</span>
          <span className="text-sm font-medium">{fechaInicioLabel ?? '—'}</span>
        </div>
      </div>
    </div>
  );
}

function IdentityCardSkeleton() {
  return (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center gap-4 mb-6 animate-pulse">
        <div className="w-16 h-16 rounded-full bg-muted" />
        <div className="space-y-2 flex-1">
          <div className="h-5 w-40 bg-muted rounded" />
          <div className="h-4 w-20 bg-muted rounded" />
        </div>
      </div>
      <div className="space-y-2 animate-pulse">
        {[0, 1].map((i) => (
          <div
            key={i}
            className={`flex justify-between py-3 ${i < 1 ? 'border-b border-border' : ''}`}
          >
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-4 w-32 bg-muted rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function IdentityCardError({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] text-center">
      <p className="font-semibold text-orange-600 mb-2">
        No pudimos cargar tu perfil
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

/** Calcula edad en años a partir de ISO date. null si no hay fecha. */
function formatEdad(fechaNacimiento: string | null): string {
  if (!fechaNacimiento) return 'Edad no registrada';
  const hoy = new Date();
  const fn = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - fn.getFullYear();
  const mesActual = hoy.getMonth();
  const mesNacimiento = fn.getMonth();
  if (
    mesActual < mesNacimiento ||
    (mesActual === mesNacimiento && hoy.getDate() < fn.getDate())
  ) {
    edad--;
  }
  return `${edad} años`;
}

/** ISO date → "1 may 2026" (locale es-AR, mes lowercase como dicta RAE). */
function formatFechaInicio(iso: string): string {
  return new Date(iso).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
